// Convert kebab-case to camelCase: my-page-asset > myPageAsset
export const camelize = s => s.replace(/-(.)/g, l => l[1].toUpperCase());

export const extractYouTubeID = url => {
  const regExp = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts|live)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
  const match = url ? url.match(regExp) : null;

  return match ? match[1] : null;
};

export const cleanListingTitle = (title, brand) => {
  if (!title) return 'Untitled';
  if (!brand) return title;

  return title.replace(new RegExp(brand, 'i'), '').trim();
};

export const decodeHtmlEntities = text => {
  if (!text || typeof text !== 'string') return text;

  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&nbsp;': ' ',
  };

  const replaced = text.replace(/&[#\w]+;/g, match => entities[match] || match);
  return replaced.charAt(0).toUpperCase() + replaced.slice(1);
};

// Helper function to format size for display (e.g., "fr40" -> "FR 40")
export const formatSize = publicData => {
  const rawSize =
    publicData?.ClothingSize || publicData?.ShoeSize || publicData?.Size || publicData.productSize;

  if (!rawSize || rawSize === 'N/A') return 'N/A';

  const normalizedSize = rawSize.trim().toLowerCase();

  // Define size prefixes to remove (ordered by specificity)
  const prefixesToRemove = ['shoe size', 'shoe-size-', 'size-', 'size'];

  let trimmed = normalizedSize;
  // Remove matching prefix
  for (const prefix of prefixesToRemove) {
    if (normalizedSize.startsWith(prefix)) {
      trimmed = normalizedSize.slice(prefix.length).trim();
      break;
    }
  }

  // Match patterns like "fr40", "fr-40", "us-10_5", "us_5_5", etc.
  const match = trimmed.match(/^([a-z]+)[-_]?(\d+(?:_\d+)?)$/i);
  if (match) {
    // Normalize to "US 10.5" format (replace underscore with period)
    const size = match[2].replace('_', '.');
    // Split into letters and numbers with space (e.g., "FR 40")
    return `${match[1].toUpperCase()} ${size}`;
  }

  // For sizes without numbers (like "M", "L", "XS"), just uppercase
  return trimmed.toUpperCase();
};

export const ABBR_TAG_REGEX = /<abbr\b[^>]*>[\s\S]*?<\/abbr>/gi;

// Regex patterns
export const PATTERNS = {
  ABBR_TAG: /<abbr\b[^>]*>[\s\S]*?<\/abbr>/gi,
  ANCHOR_TAG: /<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gis,
  GENERIC_TAG: /<[^>]+>/g,
  FULLWIDTH_TAG: /＜[^＞]+＞/g,
  BR_TAG: /<br\s*\/?>/gi,
  BLOCK_CLOSING: /<\/(p|div)>/gi,
  LIST_ITEM_CLOSING: /<\/li>/gi,
  LIST_ITEM_OPENING: /<li[^>]*>/gi,
  MULTIPLE_NEWLINES: /\n{3,}/g,
  CARRIAGE_RETURN: /\r\n/g,
};

// HTML entity mappings
const HTML_ENTITIES = {
  '&lt;': '<',
  '&gt;': '>',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
  '&#039;': "'",
  '&nbsp;': ' ',
};

/**
 * Decode common HTML entities to their character equivalents
 * @param {string} text - Text with HTML entities
 * @returns {string} Decoded text
 */
export const decodeHTMLEntities = text => {
  if (!text || typeof text !== 'string') return text;

  return text.replace(/&[#\w]+;/gi, entity => {
    return HTML_ENTITIES[entity.toLowerCase()] || entity;
  });
};

/**
 * Normalize full-width angle brackets to standard ASCII brackets
 * @param {string} text - Text with potential full-width characters
 * @returns {string} Normalized text
 */
export const normalizeBrackets = text => {
  return text.replace(/＜/g, '<').replace(/＞/g, '>');
};

/**
 * Convert anchor tags to inline text with URLs
 * @param {string} text - Text with anchor tags
 * @returns {string} Text with inlined URLs
 */
export const inlineLinks = text => {
  return text.replace(PATTERNS.ANCHOR_TAG, (match, href, linkText) => {
    const trimmedHref = href?.trim() || '';
    const trimmedText = linkText?.trim() || '';

    // If text and URL are different, show both
    if (trimmedHref && trimmedText && trimmedText !== trimmedHref) {
      return `${trimmedText} (${trimmedHref})`;
    }

    // Otherwise, just show whichever is available
    return trimmedHref || trimmedText || '';
  });
};

/**
 * Convert HTML block elements to plain text formatting
 * @param {string} text - Text with HTML block elements
 * @returns {string} Text with newlines and list formatting
 */
export const convertBlockElements = text => {
  return text
    .replace(PATTERNS.BR_TAG, '\n')
    .replace(PATTERNS.BLOCK_CLOSING, '\n\n')
    .replace(PATTERNS.LIST_ITEM_CLOSING, '\n')
    .replace(PATTERNS.LIST_ITEM_OPENING, '- ');
};

/**
 * Remove all HTML tags from text
 * @param {string} text - Text with HTML tags
 * @returns {string} Plain text
 */
export const stripTags = text => {
  return text.replace(PATTERNS.GENERIC_TAG, '').replace(PATTERNS.FULLWIDTH_TAG, '');
};

/**
 * Normalize whitespace by collapsing excessive newlines and trimming
 * @param {string} text - Text with irregular whitespace
 * @returns {string} Normalized text
 */
export const normalizeWhitespace = text => {
  return text
    .replace(PATTERNS.CARRIAGE_RETURN, '\n')
    .replace(PATTERNS.MULTIPLE_NEWLINES, '\n\n')
    .trim();
};

export function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
}

export function makeSlug(existingSlug, fallback, brandName, size) {
  let plainSlug;

  if (existingSlug && existingSlug.trim() !== '') {
    plainSlug = existingSlug;
  } else {
    plainSlug = slugify(fallback);
  }

  // add lowercase brand name and size to slug to reduce collisions
  if (brandName && brandName.trim() !== '' && !existingSlug?.includes(brandName.toLowerCase())) {
    plainSlug = `${slugify(brandName)}-${plainSlug}`;
  }
  if (size && size.trim() !== '' && ['n/a', 'na', 'NA', 'N/A'].indexOf(size.toLowerCase()) === -1) {
    plainSlug = `${plainSlug}-${slugify(size)}`;
  }

  return plainSlug;
}

export function formatHeightRange(heightRange) {
  // Remove all double quotes and spaces around the hyphen
  return heightRange
    .replace(/"\s*-\s*/, '-') // Remove quote before hyphen and spaces
    .replace(/"$/, ''); // Remove trailing quote
}

export function formatHeightString(input) {
  return input.replace(/[\uFF01-\uFF5E]/g, char => {
    // Convert fullwidth to halfwidth by subtracting the offset
    return String.fromCharCode(char.charCodeAt(0) - 0xfee0);
  });
}

export function addInchesToHeight(input) {
  if (!input || typeof input !== 'string') return input;

  // First, replace inch indicators with double quotes
  let formatted = input.replace(/(\d+)'(\d+)/g, '$1\'$2"');

  // Then, add spaces around the dash if it exists
  formatted = formatted.replace(/-/g, ' - ');

  return formatted;
}
